import React, { useCallback, useContext, useEffect, useState } from "react";
import requestCache from "../../utils/requestCache";
import { mutateErrors } from "../../utils";
import { NotifyMode } from "../notify/NotifyModeEnum";
import { useNotify } from "../notify/useNotify";
import usePrivateClassCardsQueries from "./hooks/usePrivateClassCardsQueries";
import {
  InitialPaginationConfig,
  IPaginationConfig,
} from "../program/components/Pagination";
import { IDataWithPagination } from "../../types";

export interface IPrivateClassCardTeacher {
  name: string;
  profilePicture: string;
  country: string;
  bio: string;
  education: string;
}

export interface IPrivateClassCardFrontEndModel {
  id: string;
  subjectName: string;
  teachingLanguage: string;
  teacher: IPrivateClassCardTeacher;
}

export const CardsPageContext = React.createContext<
  Partial<ReturnType<typeof useCardsPageProvider>>
>({});

export const CardsPageProvider: React.FC = ({ children }) => {
  const provider = useCardsPageProvider();

  return (
    <CardsPageContext.Provider value={provider}>
      {children}
    </CardsPageContext.Provider>
  );
};

export const useCardsPage = function () {
  return useContext(CardsPageContext);
};

export const useCardsPageProvider = function () {
  const [loading, setLoading] = useState(false);
  const [cards, setCards] = useState<IPrivateClassCardFrontEndModel[]>([]);
  const [totalCount, setTotalCount] = useState<number>(0);
  const [filters, setFilters] = useState<Record<string, any>>();
  const [paginationConfig, setPaginationConfig] = useState<IPaginationConfig>(
    InitialPaginationConfig,
  );
  const { fetchCards } = usePrivateClassCardsQueries();
  const snackNotify = useNotify();

  const handleFilterChange = useCallback((values) => {
    setFilters(values);
    setPaginationConfig((prev) => ({
      ...prev,
      page: InitialPaginationConfig.page,
    }));
  }, []);

  const getCards = useCallback(
    async (data?) => {
      setLoading(true);

      let filterOptions: Record<string, any> = {};

      if (data?.countries?.length) {
        filterOptions.countries = data.countries
          .map((country) => country.code)
          ?.join();
      }

      if (data?.subjects?.length) {
        filterOptions.subjects = data.subjects
          .map((subject) => subject.id)
          ?.join();
      }

      if (data?.teachingLanguages?.length) {
        filterOptions.teachingLanguages = data.teachingLanguages
          .map((lang) => lang.code)
          ?.join();
      }

      try {
        const cachingKey = JSON.stringify({
          ...filterOptions,
          ...paginationConfig,
        });
        const { data: responseData, totalCount } = await requestCache<
          IDataWithPagination<IPrivateClassCardFrontEndModel>
        >(cachingKey, () =>
          fetchCards({ ...filterOptions, ...paginationConfig }).then(
            (response) => response.data,
          ),
        );

        setCards(responseData);
        setTotalCount(totalCount);
        setLoading(false);
      } catch (e) {
        const { errors, message } = mutateErrors(e);
        snackNotify("Error", message, NotifyMode.ERROR);
        setLoading(false);
        return { ...errors };
      }
    },
    [paginationConfig], // eslint-disable-line react-hooks/exhaustive-deps
  );

  useEffect(() => {
    if (!filters) return;

    getCards({ ...filters, ...paginationConfig });
  }, [filters, paginationConfig, getCards]);

  return {
    handleFilterChange,
    setPaginationConfig,
    paginationConfig,
    cards,
    totalCount,
    loading,
  };
};
